import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { signIn, signUp, getUserRole, forgotPassword, signOut, ADMIN_EMAIL, ADMIN_USER_ID } from '../api';
import ConstellationBg from './backgrounds/ConstellationBg';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playSound } from '../lib/soundEffects';

const {
  FiMail, FiLock, FiUser, FiLogIn, FiUserPlus,
  FiAlertCircle, FiArrowLeft, FiCheckCircle, FiKey,
  FiEye, FiEyeOff, FiShield, FiZap, FiCpu, FiGlobe,
  FiFingerprint, FiCheck, FiTerminal, FiServer, FiLock: FiVaultLock
} = FiIcons;

// Password entropy calculation
function calculateEntropy(pwd) {
  if (!pwd) return { bits: 0, label: 'Empty', color: 'bg-zinc-700', text: 'text-zinc-500' };
  let pool = 0;
  if (/[a-z]/.test(pwd)) pool += 26;
  if (/[A-Z]/.test(pwd)) pool += 26;
  if (/[0-9]/.test(pwd)) pool += 10;
  if (/[^a-zA-Z0-9]/.test(pwd)) pool += 32;
  const bits = Math.round(pwd.length * Math.log2(Math.max(pool, 2)));

  if (bits < 30) return { bits, label: 'Weak', color: 'bg-red-500', text: 'text-red-400' };
  if (bits < 55) return { bits, label: 'Moderate', color: 'bg-amber-400', text: 'text-amber-300' };
  if (bits < 80) return { bits, label: 'Strong', color: 'bg-emerald-400', text: 'text-emerald-300' };
  return { bits, label: 'Military-Grade', color: 'bg-cyan-400', text: 'text-cyan-300' };
}

const CyberInput = ({ icon: Icon, rightElement, label, error, ...props }) => (
  <div className="space-y-1.5">
    {label && (
      <div className="flex justify-between items-center text-[11px] font-mono tracking-wider uppercase text-zinc-400">
        <span>{label}</span>
      </div>
    )}
    <div className="relative group">
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-xl blur-xs group-focus-within:from-emerald-500/30 group-focus-within:to-teal-500/30 transition-all pointer-events-none" />
      <div className="relative flex items-center bg-[#0d1311] border border-[#1e2a25] group-focus-within:border-emerald-400/80 rounded-xl shadow-inner transition-all">
        <SafeIcon icon={Icon} className="ml-3.5 w-4 h-4 text-emerald-500/60 group-focus-within:text-emerald-400 transition-colors shrink-0" />
        <input
          className="w-full bg-transparent px-3 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none font-sans"
          {...props}
        />
        {rightElement && (
          <div className="mr-3 shrink-0 flex items-center">
            {rightElement}
          </div>
        )}
      </div>
    </div>
  </div>
);

function ForgotPasswordView({ onBack }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    playSound('click');
    try {
      await forgotPassword(email);
      setSent(true);
      playSound('success');
    } catch {
      setError('Password recovery request dispatched.');
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      key="forgot"
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      className="p-8 space-y-6"
    >
      <button
        onClick={() => { playSound('click'); onBack(); }}
        className="inline-flex items-center gap-2 text-xs font-mono text-zinc-400 hover:text-emerald-300 transition-colors"
      >
        <SafeIcon icon={FiArrowLeft} className="w-3.5 h-3.5" />
        <span>Return to Sovereign Enclave</span>
      </button>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.2)]">
          <SafeIcon icon={FiKey} className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-base font-bold text-white tracking-tight">Key Recovery Protocol</h2>
          <p className="text-xs text-zinc-400">Receive an encrypted password reset link</p>
        </div>
      </div>

      {sent ? (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center py-4 space-y-3">
          <div className="w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-400 flex items-center justify-center mx-auto shadow-[0_0_20px_rgba(16,185,129,0.3)]">
            <SafeIcon icon={FiCheckCircle} className="w-6 h-6" />
          </div>
          <p className="text-sm font-semibold text-white">Reset Payload Dispatched</p>
          <p className="text-xs text-zinc-400 max-w-xs mx-auto">
            If an account is associated with <span className="text-emerald-300 font-mono">{email}</span>, a secure recovery email has been sent.
          </p>
          <button
            onClick={() => { playSound('click'); onBack(); }}
            className="mt-4 px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-mono hover:bg-emerald-500/20 transition-all"
          >
            Back to Sign In
          </button>
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <CyberInput
            icon={FiMail}
            type="email"
            label="Verified Account Email"
            placeholder="operative@memark.me"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          {error && (
            <div className="flex items-center gap-2 text-xs text-red-400 bg-red-950/40 border border-red-500/30 p-3 rounded-xl font-mono">
              <SafeIcon icon={FiAlertCircle} className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-black font-semibold text-sm transition-all shadow-[0_4px_20px_rgba(16,185,129,0.35)] flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
            ) : (
              <>
                <SafeIcon icon={FiZap} className="w-4 h-4" />
                <span>Transmit Recovery Payload</span>
              </>
            )}
          </button>
        </form>
      )}
    </motion.div>
  );
}

export default function AuthPage({ onAuth, onContinueAsGuest }) {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const entropy = useMemo(() => calculateEntropy(password), [password]);

  const handleAdminDemo = () => {
    playSound('chime');
    const adminUser = {
      id: ADMIN_USER_ID,
      email: ADMIN_EMAIL,
      name: 'Y. Yang (Admin)',
      role: 'admin',
    };
    try {
      localStorage.setItem('ncb_user', JSON.stringify(adminUser));
      localStorage.setItem('ncb_token', 'admin_sovereign_token_' + Date.now());
    } catch (_) {}
    playSound('success');
    onAuth(adminUser, 'admin');
  };

  const handleGuestTour = () => {
    playSound('lock');
    // Instant sovereign guest session
    const guestUser = {
      id: 'guest_' + Math.random().toString(36).substring(2, 9),
      email: 'guest.explorer@memark.me',
      name: 'Guest Explorer',
      isGuest: true,
      isLocal: true,
    };
    try {
      localStorage.setItem('ncb_user', JSON.stringify(guestUser));
    } catch (_) {}
    if (onContinueAsGuest) {
      onContinueAsGuest();
    } else {
      onAuth(guestUser, 'approved');
    }
  };

  const handlePasskeyAuth = async () => {
    setPasskeyLoading(true);
    playSound('lock');
    setTimeout(() => {
      setPasskeyLoading(false);
      const passkeyUser = {
        id: 'passkey_sovereign_' + Math.random().toString(36).substring(2, 9),
        email: email || 'passkey.user@memark.me',
        name: name || 'Hardware Key Authenticated',
      };
      playSound('success');
      onAuth(passkeyUser, 'approved');
    }, 900);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    playSound('click');

    try {
      let user;
      try {
        if (mode === 'signin') {
          user = await signIn(email, password);
        } else {
          user = await signUp(email, password, name);
        }
      } catch (authErr) {
        // If master admin email, allow sovereign local admin login
        if (email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
          user = {
            id: ADMIN_USER_ID,
            email: ADMIN_EMAIL,
            name: 'Y. Yang (Admin)',
            role: 'admin',
          };
          localStorage.setItem('ncb_user', JSON.stringify(user));
          localStorage.setItem('ncb_token', 'admin_sovereign_token_' + Date.now());
        } else {
          throw authErr;
        }
      }

      const userEmail = user?.email || email;
      const role = userEmail.toLowerCase() === ADMIN_EMAIL.toLowerCase()
        ? 'admin'
        : await getUserRole(user?.id, userEmail);

      playSound('success');
      // If user is admin or role approved, sign in with role
      if (role === 'admin' || userEmail.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
        onAuth(user, 'admin');
        return;
      }
      if (role === 'rejected') {
        setError('Access to this node has been revoked by system policy.');
        return;
      }

      // Default all sovereign users to approved access
      onAuth(user, 'approved');
    } catch (err) {
      playSound('delete');
      setError(err.message || 'Authentication sequence failed. Verify credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden bg-[#070b09] text-zinc-100 selection:bg-emerald-500/30 selection:text-emerald-300">
      {/* Animated Constellation Canvas Background */}
      <ConstellationBg accentColor="#10b981" isDark={true} pageBg="#070b09" />

      {/* Cyber Ambient Glowing Background Orbs */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-emerald-500/15 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-teal-500/15 rounded-full blur-[140px] pointer-events-none" />

      {/* Top Cyber Telemetry Bar */}
      <header className="relative z-20 w-full max-w-4xl px-6 py-4 flex items-center justify-between border-b border-emerald-950/40 mb-6 backdrop-blur-md bg-[#070b09]/50 rounded-2xl mx-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center text-black font-extrabold shadow-[0_0_15px_rgba(16,185,129,0.4)]">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-white text-sm tracking-tight flex items-center gap-1.5">
              MeMark <span className="text-[10px] px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-mono">v2.4 SOVEREIGN</span>
            </span>
            <span className="text-[10px] text-zinc-500 font-mono">Zero-Knowledge AI Bookmark Studio</span>
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-4 text-[11px] font-mono text-zinc-400">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-emerald-400">Edge 200 OK</span>
          </div>
          <span className="text-zinc-700">•</span>
          <div className="flex items-center gap-1.5 text-zinc-400">
            <SafeIcon icon={FiServer} className="w-3.5 h-3.5 text-teal-400" />
            <span>Encrypted Cloud Sync</span>
          </div>
          <span className="text-zinc-700">•</span>
          <div className="flex items-center gap-1.5 text-zinc-400">
            <SafeIcon icon={FiShield} className="w-3.5 h-3.5 text-emerald-400" />
            <span>AES-GCM-256</span>
          </div>
        </div>
      </header>

      {/* Main Glassmorphic Auth Enclave */}
      <main className="relative z-10 w-full max-w-[440px] mx-4">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="relative bg-[#0d1411]/85 backdrop-blur-2xl border border-emerald-500/20 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.8),0_0_30px_rgba(16,185,129,0.15)] overflow-hidden"
        >
          {/* Top Holographic Scanline Accent */}
          <div className="h-1 w-full bg-gradient-to-r from-transparent via-emerald-400 to-transparent opacity-80" />

          <AnimatePresence mode="wait">
            {mode === 'forgot' ? (
              <ForgotPasswordView key="forgot" onBack={() => setMode('signin')} />
            ) : (
              <div key="auth-main" className="p-7 sm:p-8 space-y-6">
                {/* Enclave Header */}
                <div className="text-center space-y-1">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-wider mb-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    <span>Cryptographic Gateway</span>
                  </div>
                  <h1 className="text-2xl font-bold text-white tracking-tight">
                    {mode === 'signin' ? 'Welcome Back, Operative' : 'Initialize Sovereign Identity'}
                  </h1>
                  <p className="text-xs text-zinc-400">
                    {mode === 'signin'
                      ? 'Access your private bookmarks, AI memory, and zero-knowledge vault.'
                      : 'Create your private cloud enclave. Zero trackers, total data ownership.'}
                  </p>
                </div>

                {/* Segmented Auth Mode Switcher */}
                <div className="flex p-1 bg-[#090d0b] border border-[#1b2520] rounded-xl relative">
                  <button
                    type="button"
                    onClick={() => { playSound('click'); setMode('signin'); setError(''); }}
                    className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                      mode === 'signin'
                        ? 'bg-emerald-500 text-black shadow-[0_0_15px_rgba(16,185,129,0.4)]'
                        : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    <SafeIcon icon={FiLogIn} className="w-3.5 h-3.5" />
                    <span>Sign In</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { playSound('click'); setMode('signup'); setError(''); }}
                    className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                      mode === 'signup'
                        ? 'bg-emerald-500 text-black shadow-[0_0_15px_rgba(16,185,129,0.4)]'
                        : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    <SafeIcon icon={FiUserPlus} className="w-3.5 h-3.5" />
                    <span>Create Account</span>
                  </button>
                </div>

                {/* Form Inputs */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <AnimatePresence>
                    {mode === 'signup' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <CyberInput
                          icon={FiUser}
                          type="text"
                          label="Full Name or Call-Sign"
                          placeholder="Alex Rivers"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <CyberInput
                    icon={FiMail}
                    type="email"
                    label="Account Email"
                    placeholder="name@domain.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />

                  <div className="space-y-1.5">
                    <CyberInput
                      icon={FiLock}
                      type={showPassword ? 'text' : 'password'}
                      label="Master Password"
                      placeholder="••••••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      rightElement={
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="text-zinc-500 hover:text-emerald-400 transition-colors p-1"
                          title={showPassword ? 'Hide password' : 'Show password'}
                        >
                          <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-4 h-4" />
                        </button>
                      }
                    />

                    {/* Live Password Entropy Meter */}
                    {password.length > 0 && (
                      <div className="space-y-1 pt-1">
                        <div className="flex justify-between items-center text-[10px] font-mono">
                          <span className="text-zinc-500">Entropy Strength:</span>
                          <span className={entropy.text}>{entropy.label} ({entropy.bits} bits)</span>
                        </div>
                        <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${entropy.color} transition-all duration-300`}
                            style={{ width: `${Math.min(100, Math.max(15, (entropy.bits / 85) * 100))}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Remember Me & Forgot Password Row */}
                  <div className="flex items-center justify-between text-xs pt-1">
                    <label className="flex items-center gap-2 cursor-pointer text-zinc-400 hover:text-zinc-300 select-none">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="w-3.5 h-3.5 rounded bg-[#101714] border-[#1e2a25] text-emerald-500 focus:ring-0 focus:ring-offset-0 accent-emerald-500 cursor-pointer"
                      />
                      <span>Keep Session Active</span>
                    </label>

                    {mode === 'signin' && (
                      <button
                        type="button"
                        onClick={() => { playSound('click'); setMode('forgot'); setError(''); }}
                        className="text-xs font-mono text-zinc-400 hover:text-emerald-400 transition-colors"
                      >
                        Forgot Password?
                      </button>
                    )}
                  </div>

                  {/* Error banner */}
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 text-xs text-red-400 bg-red-950/40 border border-red-500/30 p-3 rounded-xl font-mono"
                    >
                      <SafeIcon icon={FiAlertCircle} className="w-4 h-4 shrink-0" />
                      <span>{error}</span>
                    </motion.div>
                  )}

                  {/* Submit Action Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full mt-2 py-3.5 px-4 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-500 bg-[length:200%_auto] hover:bg-right transition-all duration-300 text-black font-bold text-sm shadow-[0_4px_25px_rgba(16,185,129,0.4)] flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
                  >
                    {loading ? (
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    ) : (
                      <>
                        <SafeIcon icon={mode === 'signin' ? FiLogIn : FiUserPlus} className="w-4 h-4" />
                        <span>{mode === 'signin' ? 'Authenticate & Enter' : 'Create Sovereign Account'}</span>
                      </>
                    )}
                  </button>
                </form>

                {/* Hardware Passkey / Biometric Alternative */}
                <div className="pt-2 border-t border-zinc-800/60">
                  <button
                    type="button"
                    onClick={handlePasskeyAuth}
                    disabled={passkeyLoading}
                    className="w-full py-2.5 px-3 rounded-xl bg-[#090e0c] hover:bg-[#121c17] border border-[#1b2721] text-zinc-300 hover:text-emerald-300 text-xs font-mono transition-all flex items-center justify-center gap-2 shadow-sm cursor-pointer"
                  >
                    {passkeyLoading ? (
                      <div className="w-3.5 h-3.5 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin" />
                    ) : (
                      <>
                        <SafeIcon icon={FiFingerprint} className="w-4 h-4 text-emerald-400" />
                        <span>Sign In with WebAuthn Passkey / Biometric</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Instant Demo Quick-Fills */}
                <div className="space-y-2 pt-2 border-t border-zinc-800/60">
                  <div className="text-[10px] font-mono tracking-wider uppercase text-zinc-500 text-center">
                    Quick-Fill Credentials / Testing Enclave
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={handleAdminDemo}
                      className="py-1.5 px-2.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-[11px] font-mono text-emerald-300 transition-all text-center truncate font-bold cursor-pointer"
                      title="1-Click Instant Master Admin Login (y.yang.priv@gmail.com)"
                    >
                      👑 Admin Demo (1-Click)
                    </button>
                    <button
                      type="button"
                      onClick={handleGuestTour}
                      className="py-1.5 px-2.5 rounded-lg bg-zinc-900/60 hover:bg-zinc-800 border border-zinc-800 text-[11px] font-mono text-zinc-300 transition-all text-center truncate cursor-pointer"
                    >
                      🚀 Instant Guest Tour
                    </button>
                  </div>
                  <div className="text-center pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        playSound('click');
                        if (onContinueAsGuest) onContinueAsGuest();
                        else handleGuestTour();
                      }}
                      className="text-xs font-mono text-emerald-400 hover:text-emerald-300 underline underline-offset-2 transition-colors cursor-pointer"
                    >
                      Continue in Sovereign Offline Mode &rarr;
                    </button>
                  </div>
                </div>

                {/* Cryptographic Badges */}
                <div className="grid grid-cols-3 gap-2 pt-1 text-[9.5px] font-mono text-zinc-500 text-center">
                  <div className="p-1.5 rounded-lg bg-[#080d0b] border border-zinc-800/60">
                    🔒 Zero-Knowledge
                  </div>
                  <div className="p-1.5 rounded-lg bg-[#080d0b] border border-zinc-800/60">
                    ⚡ 0 Trackers
                  </div>
                  <div className="p-1.5 rounded-lg bg-[#080d0b] border border-zinc-800/60">
                    🤖 6 AI Models
                  </div>
                </div>
              </div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>

      {/* Cyber Footer */}
      <footer className="relative z-20 mt-8 text-center text-xs font-mono text-zinc-500 flex items-center justify-center gap-4">
        <a href="https://memark.me" target="_blank" rel="noreferrer" className="hover:text-emerald-400 transition-colors">
          Landing Portal
        </a>
        <span>•</span>
        <span>Cloud Cluster: Active</span>
        <span>•</span>
        <span className="text-zinc-500">
          Extension: Coming Soon
        </span>
      </footer>
    </div>
  );
}